import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SiteSetting {
  id: string;
  key: string;
  value: string | null;
  created_at: string;
  updated_at: string;
}

export function useSiteSettings() {
  return useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*');
      
      if (error) throw error;
      return data as SiteSetting[];
    },
  });
}

export function useSocialLinks() {
  const { data: settings, isLoading } = useSiteSettings();
  
  const socialLinks = {
    linkedin: settings?.find(s => s.key === 'social_linkedin')?.value || '',
    x: settings?.find(s => s.key === 'social_x')?.value || '',
    instagram: settings?.find(s => s.key === 'social_instagram')?.value || '',
    facebook: settings?.find(s => s.key === 'social_facebook')?.value || '',
  };
  
  return { socialLinks, isLoading };
}

export function useHomepageWidgets() {
  const { data: settings, isLoading } = useSiteSettings();
  
  return {
    showEventsWidget: settings?.find(s => s.key === 'show_events_widget')?.value !== 'false',
    showJobsWidget: settings?.find(s => s.key === 'show_jobs_widget')?.value !== 'false',
    isLoading
  };
}

export function useUpdateSiteSetting() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from('site_settings')
        .update({ value })
        .eq('key', key);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
    },
  });
}
